import { FORM_ERROR } from "final-form"
import { navigate } from "gatsby"
import { useEffect, useMemo, useState } from "react"
import { Form } from "react-final-form"

import { marketingClient } from "@trueskin-web/apis"
import {
  Button,
  EmailAutocomplete,
  FieldError,
  Icon,
} from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import {
  composeValidators,
  getFormValidationErrors,
  getServerValidationErrorTypes,
  getSignUpError,
  mustBeEmail,
  required,
  sleep,
} from "@trueskin-web/functions"
import { getCountry } from "@trueskin-web/locales"
import { trackingService } from "@trueskin-web/services"
import { arrowLongRightIcon } from "@trueskin-web/theme/icons/arrowLongRightIcon"
import { Routes } from "@trueskin-web/translations"

import domains from "../../start/create-account/domains"
import ExistingAccountDialog from "../../start/create-account/existing-account-dialog"

import StarRating from "./star-rating"
import TermsCheckbox from "./terms-checkbox"

const SignupForm = ({
  token,
  provider,
  providerID,
  email,
  buttonLabel,
  dialogImage,
}) => {
  const { register, user } = useAuth()

  const [existingEmail, setExistingEmail] = useState(null)

  const validateEmail = useMemo(
    () => composeValidators(required, mustBeEmail),
    []
  )

  const handleSubmit = async ({ email, newsletterSubscription }) => {
    try {
      await register({
        email,
        provider,
        providerID,
        newsletterSubscription,
        signedUpWithEmailOnly: true,
        tempToken: token,
        country: getCountry(),
        campaignTracking: trackingService.getTrackingTouchpoints(),
        touchPoints: trackingService.getAllTrackingTouchPoints(),
      })

      trackingService.clearTrackingTouchpoints()

      // allow time for states to synchronize
      await sleep(1500)
    } catch (error) {
      handleSubmitError({ error, email })

      return {
        [FORM_ERROR]: getSignUpError({
          error,
          action: handleClickLoginLink,
          cta: Button,
        }),
      }
    }
  }

  const handleFormValidationErrors = (errors) => {
    const errorTypes = getFormValidationErrors(errors)

    errorTypes.forEach(trackCreateAccountError)
  }

  const handleSubmitError = ({ error, email }) => {
    if (error.USERNAME_OR_EMAIL_TAKEN) {
      const errorTypes = getServerValidationErrorTypes()

      trackCreateAccountError(errorTypes.email_existing)

      setExistingEmail(email)
    }
  }

  const trackCreateAccountError = (errorType) =>
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.ACCOUNT_CREATION_ERROR,
      data: {
        errorType,
      },
    })

  const handleClickLoginLink = () => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.LOGIN_FROM_ERROR_MESSAGE,
    })

    Object.assign(document.createElement("a"), {
      href: Routes.App.Login.url,
    }).click()
  }

  useEffect(() => {
    if (user) {
      navigate(Routes.App.Start.url)
    }
  }, [user])

  return (
    <>
      <Form
        initialValues={{
          email,
          agreeTerms: provider === "apple",
          newsletterSubscription: false,
        }}
        onSubmit={handleSubmit}
        render={({
          handleSubmit,
          form,
          submitting,
          errors,
          submitError,
          hasValidationErrors,
          dirtySinceLastSubmit,
        }) => (
          <form
            data-testid="web-portal-create-account-form"
            onSubmit={(event) => {
              if (hasValidationErrors) {
                handleFormValidationErrors(errors)
              }
              handleSubmit(event)
            }}
            noValidate
            sx={{ mt: 5, mb: 6 }}
          >
            <EmailAutocomplete
              placeholder="Deine E-Mail-Adresse"
              domains={domains}
              validate={validateEmail}
              isDisabled={submitting}
              onInputChange={(value) => form.change("email", value)}
            />

            <Button
              data-testid="web-portal-create-account-submit"
              type="submit"
              isLoading={submitting}
              rounded
              isFullWidth
              sx={{ mt: 3, alignItems: "center", height: "44px" }}
            >
              {buttonLabel} <Icon icon={arrowLongRightIcon} />
            </Button>

            {submitError && !dirtySinceLastSubmit && (
              <FieldError
                sx={{
                  color: "#FF475D",
                }}
              >
                {submitError}
              </FieldError>
            )}

            <StarRating />

            <TermsCheckbox />
          </form>
        )}
      />
      {existingEmail && (
        <ExistingAccountDialog
          email={existingEmail}
          image={dialogImage}
          onDismiss={() => setExistingEmail(null)}
        />
      )}
    </>
  )
}

export default SignupForm
